import { createBrowserRouter, Navigate, type RouteObject } from 'react-router'
import CheckEmail from '~/pages/join/CheckEmail'
import ConfirmEmail from '~/pages/join/ConfirmEmail'
import DocumentReturn from '~/pages/join/document/DocumentReturn'
import DocumentStep from '~/pages/join/document/DocumentStep'
import Continue from '~/pages/join/Continue'
import AboutYou from '~/pages/join/AboutYou'
import JoinRoute from '~/pages/join/JoinRoute'
import WhereYouLive from '~/pages/join/WhereYouLive'
import Welcome from '~/pages/join/Welcome'
import FaceStep from '~/biometrics/FaceStep'
import ReturnPage from '~/biometrics/ReturnPage'
import WaitPage from '~/biometrics/Wait'
import Cookies from '~/pages/legal/Cookies'
import Privacy from '~/pages/legal/Privacy'
import Contact from '~/pages/member/Contact'
import Data from '~/pages/member/Data'
import DeleteMyData from '~/pages/member/DeleteMyData'
import DeleteRegistration from '~/pages/member/DeleteRegistration'
import Forgot from '~/pages/member/Forgot'
import JoinStatus from '~/pages/member/JoinStatus'
import Login from '~/pages/member/Login'
import LoginLink from '~/pages/member/LoginLink'
import MemberFace from '~/pages/member/MemberFace'
import MemberShell from '~/pages/member/MemberShell'
import Overview from '~/pages/member/Overview'
import SignIn from '~/pages/member/SignIn'
import Profile from '~/pages/member/Profile'
import Reset from '~/pages/member/Reset'
import NotFound from '~/pages/NotFound'
import Unsubscribe from '~/pages/Unsubscribe'

/** The owner's AWS device-matrix page: only in a dev build or with VITE_DEV_PAGES=true, never in production. */
const devRoutes: RouteObject[] =
  import.meta.env.DEV || import.meta.env.VITE_DEV_PAGES === 'true'
    ? [
        {
          path: 'dev/aws-liveness',
          lazy: async () => ({ Component: (await import('~/pages/dev/AwsLiveness')).default }),
        },
      ]
    : []
/** Lazy route module: the admin code (and Leaflet) is loaded only when an /admin page is opened, never for members. */
const lazyPage = (load: () => Promise<{ default: React.ComponentType }>) => async () => ({
  Component: (await load()).default,
})

/**
 * T20: the admin pages under /admin. `login`, `invite`, `forgot` and `reset` (R108) are public; everything else needs
 * an admin session.
 */
function adminRoutes(): RouteObject {
  return {
    path: 'admin',
    children: [
      { path: 'login', lazy: lazyPage(() => import('~/admin/pages/Login')) },
      { path: 'invite', lazy: lazyPage(() => import('~/admin/pages/InviteAccept')) },
      { path: 'forgot', lazy: lazyPage(() => import('~/admin/pages/Forgot')) }, // R108
      { path: 'reset', lazy: lazyPage(() => import('~/admin/pages/Reset')) }, // R108
      {
        lazy: lazyPage(() => import('~/admin/RequireAdmin')),
        children: [
          {
            lazy: lazyPage(() => import('~/admin/AdminLayout')),
            children: [
              { index: true, lazy: lazyPage(() => import('~/admin/pages/Overview')) },
              { path: 'members', lazy: lazyPage(() => import('~/admin/pages/Members')) },
              { path: 'discarded', lazy: lazyPage(() => import('~/admin/pages/Discarded')) }, // R112
              { path: 'members/:id', lazy: lazyPage(() => import('~/admin/pages/MemberDetail')) },
              { path: 'review', lazy: lazyPage(() => import('~/admin/pages/ReviewQueue')) },
              { path: 'map', lazy: lazyPage(() => import('~/admin/pages/Map')) },
              { path: 'groups', lazy: lazyPage(() => import('~/admin/pages/Groups')) },
              { path: 'communications', lazy: lazyPage(() => import('~/admin/pages/Communications')) },
              { path: 'messages', lazy: lazyPage(() => import('~/admin/pages/Messages')) },
              { path: 'providers', lazy: lazyPage(() => import('~/admin/pages/Providers')) },
              { path: 'compare', lazy: lazyPage(() => import('~/admin/pages/Compare')) },
              { path: 'blocklist', lazy: lazyPage(() => import('~/admin/pages/Blocklist')) },
              { path: 'admins', lazy: lazyPage(() => import('~/admin/pages/Admins')) },
              { path: 'audit', lazy: lazyPage(() => import('~/admin/pages/Audit')) },
            ],
          },
        ],
      },
    ],
  }
}

/**
 * Route map. Later tasks append their routes (T19: the `face`, `wait`, `status`, `return` and `delete` slots below
 * and /login; T20: /admin/...); never reorder or rewrite another task's entries.
 *
 *   /join, /join/:providerId          S0 welcome (or "link not active")
 *   /join/:providerId/about           S1      /email  S2      /place  S3      /document  S3b (5-step flow)
 *   /join/:providerId/face|wait|status  S4, S4-wait, S6 (S6 claims the voter session, then /me)
 *   /join/confirm, /join/continue     pages behind the email buttons (token in the URL fragment)
 *   /join/delete, /join/return, /join/document/return   delete my registration (token in the fragment), hosted return page
 *   /login, /login/link               voter sign in (the sign-in email: a link and a code; or the password)
 *   /forgot, /reset                   forgotten password, new password (token in the fragment)
 *   /me, /me/profile|contact|sign-in|data|delete   voter area (sidebar shell); /me/face face check again (R32)
 *   /dev/aws-liveness                 AWS device matrix page (dev builds only)
 *   /unsubscribe/:token               R78: one button that POSTs
 *   /privacy, /cookies                legal pages
 *   anything else                     "This page does not exist" (not a redirect)
 */
export const routeObjects: RouteObject[] = [
  { index: true, element: <Navigate to='/join' replace /> },
  {
    path: 'join',
    children: [
      { path: 'confirm', element: <ConfirmEmail /> },
      { path: 'continue', element: <Continue /> },
      { path: 'delete', element: <DeleteRegistration /> }, // T19
      { path: 'return', element: <ReturnPage /> }, // T19
      { path: 'document/return', element: <DocumentReturn /> }, // T25
      { element: <JoinRoute />, children: [{ index: true, element: <Welcome /> }] },
      {
        path: ':providerId',
        children: [
          {
            element: <JoinRoute />,
            children: [
              { index: true, element: <Welcome /> },
              { path: 'about', element: <AboutYou /> },
            ],
          },
          { path: 'email', element: <CheckEmail /> },
          { path: 'place', element: <WhereYouLive /> },
          { path: 'document', element: <DocumentStep /> }, // T25
          { path: 'face', element: <FaceStep /> }, // T19
          { path: 'wait', element: <WaitPage /> }, // T19
          { path: 'status', element: <JoinStatus /> }, // T19
        ],
      },
      // T23: the FaceTec test check page (its own CSP; entered and left by a full navigation, never a route change)
      { path: 'facetec/capture', lazy: () => import('~/biometrics/providers/facetec/CapturePage') },
    ],
  },
  { path: 'login', element: <Login /> },
  { path: 'login/link', element: <LoginLink /> },
  { path: 'forgot', element: <Forgot /> },
  { path: 'reset', element: <Reset /> },
  {
    path: 'me',
    element: <MemberShell />,
    children: [
      { index: true, element: <Overview /> },
      { path: 'profile', element: <Profile /> },
      { path: 'contact', element: <Contact /> },
      { path: 'sign-in', element: <SignIn /> },
      // The old address of the section ("Change my password"), kept for bookmarks.
      { path: 'password', element: <Navigate to='/me/sign-in' replace /> },
      { path: 'data', element: <Data /> },
      { path: 'delete', element: <DeleteMyData /> },
    ],
  },
  // The face check again is a focused flow with its own page frame (and help sheet), outside the sidebar shell.
  { path: 'me/face', element: <MemberFace /> },
  ...devRoutes,
  { path: 'unsubscribe/:token', element: <Unsubscribe /> },
  { path: 'privacy', element: <Privacy /> },
  { path: 'cookies', element: <Cookies /> },
  adminRoutes(), // T20
  { path: '*', element: <NotFound /> },
]

export const createRouter = () => createBrowserRouter(routeObjects)
